/**
 * Bulk Analysis Filter Utilities
 * Efficient filtering algorithms for large race datasets
 */

import { logger } from './logger';

/**
 * Apply all filters to a race dataset
 * @param {Array} races - Array of race objects
 * @param {Object} filters - Filter configuration object
 * @returns {Array} Filtered race array
 */
export const applyFilters = (races, filters) => {
  if (!races || !Array.isArray(races)) {
    logger.warn('applyFilters: Invalid races data provided');
    return [];
  }

  if (!filters) {
    return races;
  }

  let filteredRaces = races;

  // Apply date range filter
  if (filters.dateRange?.start || filters.dateRange?.end) {
    filteredRaces = filterByDateRange(filteredRaces, filters.dateRange);
  }

  // Apply days of week filter
  if (filters.daysOfWeek?.length > 0) {
    filteredRaces = filterByDaysOfWeek(filteredRaces, filters.daysOfWeek);
  }

  // Apply states filter
  if (filters.states?.length > 0) {
    filteredRaces = filterByStates(filteredRaces, filters.states);
  }

  // Apply tracks filter
  if (filters.tracks?.length > 0) {
    filteredRaces = filterByTracks(filteredRaces, filters.tracks);
  }

  // Apply combined odds threshold filter
  if (filters.combinedOddsThreshold !== null && filters.combinedOddsThreshold !== undefined) {
    filteredRaces = filterByCombinedOdds(filteredRaces, filters.combinedOddsThreshold);
  }

  // Apply horse count range filter
  if (
    filters.horseCountRange &&
    (filters.horseCountRange.min !== null &&
      filters.horseCountRange.min !== undefined ||
      filters.horseCountRange.max !== null &&
      filters.horseCountRange.max !== undefined)
  ) {
    filteredRaces = filterByHorseCount(filteredRaces, filters.horseCountRange);
  }

  logger.log(`applyFilters: Filtered ${races.length} races down to ${filteredRaces.length}`);
  return filteredRaces;
};

/**
 * Filter races by date range
 * @param {Array} races - Race array
 * @param {Object} dateRange - {start: string, end: string}
 * @returns {Array} Filtered races
 */
export const filterByDateRange = (races, dateRange) => {
  const { start, end } = dateRange;
  
  return races.filter(race => {
    const raceDate = new Date(race.date || race.raceDate);
    
    if (start && raceDate < new Date(start)) {
      return false;
    }
    
    if (end && raceDate > new Date(end)) {
      return false;
    }
    
    return true;
  });
};

/**
 * Filter races by days of week
 * @param {Array} races - Race array
 * @param {Array} daysOfWeek - Array of day names
 * @returns {Array} Filtered races
 */
export const filterByDaysOfWeek = (races, daysOfWeek) => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  return races.filter(race => {
    const raceDate = new Date(race.date || race.raceDate);
    const dayName = dayNames[raceDate.getDay()];
    return daysOfWeek.includes(dayName);
  });
};

/**
 * Filter races by states
 * @param {Array} races - Race array
 * @param {Array} states - Array of state codes
 * @returns {Array} Filtered races
 */
export const filterByStates = (races, states) => {
  return races.filter(race => {
    const raceState = race.state || race.trackState || extractStateFromTrack(race.track);
    return states.includes(raceState);
  });
};

/**
 * Filter races by tracks
 * @param {Array} races - Race array
 * @param {Array} tracks - Array of track names
 * @returns {Array} Filtered races
 */
export const filterByTracks = (races, tracks) => {
  return races.filter(race => {
    const trackName = race.track || race.trackName;
    return tracks.includes(trackName);
  });
};

/**
 * Filter races by combined odds threshold
 * @param {Array} races - Race array
 * @param {number} threshold - Maximum combined odds
 * @returns {Array} Filtered races
 */
export const filterByCombinedOdds = (races, threshold) => {
  return races.filter(race => {
    const combinedOdds = calculateCombinedOdds(race);
    return combinedOdds <= threshold;
  });
};

/**
 * Filter races by horse count range
 * @param {Array} races - Race array
 * @param {Object} range - {min: number, max: number}
 * @returns {Array} Filtered races
 */
export const filterByHorseCount = (races, range) => {
  if (!range) return races;
  const { min = null, max = null } = range;
  
  return races.filter(race => {
    const horseCount = getHorseCount(race);
    
    if (min !== null && min !== undefined && horseCount < min) {
      return false;
    }
    
    if (max !== null && max !== undefined && horseCount > max) {
      return false;
    }
    
    return true;
  });
};

/**
 * Calculate combined odds for all tips in a race
 * @param {Object} race - Race object
 * @returns {number} Combined odds value
 */
export const calculateCombinedOdds = (race) => {
  if (!race.tips || !Array.isArray(race.tips)) {
    return 0;
  }

  // Sum all tip odds
  const totalOdds = race.tips.reduce((sum, tip) => {
    const odds = parseFloat(tip.odds || tip.price || 0);
    return sum + odds;
  }, 0);

  return totalOdds;
};

/**
 * Get horse count for a race
 * @param {Object} race - Race object
 * @returns {number} Number of horses in race
 */
export const getHorseCount = (race) => {
  if (race.horseCount) {
    return parseInt(race.horseCount);
  }
  
  if (race.horses && Array.isArray(race.horses)) {
    return race.horses.length;
  }
  
  if (race.tips && Array.isArray(race.tips)) {
    return race.tips.length;
  }
  
  // Default fallback
  return 0;
};

/**
 * Extract state from track name using known mappings
 * @param {string} trackName - Name of the track
 * @returns {string} State code
 */
export const extractStateFromTrack = (trackName) => {
  // This would ideally use the track-names.json data
  // For now, return a default or try to infer from common patterns
  const trackStateMap = {
    'Flemington': 'VIC',
    'Caulfield': 'VIC',
    'Moonee Valley': 'VIC',
    'Randwick': 'NSW',
    'Rosehill': 'NSW',
    'Canterbury': 'NSW',
    'Eagle Farm': 'QLD',
    'Doomben': 'QLD',
    'Gold Coast': 'QLD',
    'Morphettville': 'SA',
    'Ascot': 'WA',
    'Belmont Park': 'WA',
    'Hobart': 'TAS',
    'Darwin': 'NT',
    'Canberra': 'ACT'
  };
  
  return trackStateMap[trackName] || 'NSW'; // Default to NSW
};

/**
 * Extract available filter options from race data
 * @param {Array} races - Array of race objects
 * @returns {Object} Available filter options
 */
export const extractFilterOptions = (races) => {
  if (!races || !Array.isArray(races)) {
    return {
      states: [],
      tracks: [],
      dateRange: { earliest: null, latest: null },
      horseCountRange: { min: 1, max: 30 }
    };
  }

  const states = new Set();
  const tracks = new Set();
  const dates = [];
  const horseCounts = [];

  races.forEach(race => {
    // Extract states
    const state = race.state || race.trackState || extractStateFromTrack(race.track);
    if (state) states.add(state);

    // Extract tracks
    const track = race.track || race.trackName;
    if (track) tracks.add(track);

    // Extract dates
    const date = race.date || race.raceDate;
    if (date) dates.push(new Date(date));

    // Extract horse counts
    const horseCount = getHorseCount(race);
    if (horseCount > 0) horseCounts.push(horseCount);
  });

  // Sort dates to find range
  dates.sort((a, b) => a - b);
  
  return {
    states: Array.from(states).sort(),
    tracks: Array.from(tracks).sort(),
    dateRange: {
      earliest: dates.length > 0 ? dates[0].toISOString().split('T')[0] : null,
      latest: dates.length > 0 ? dates[dates.length - 1].toISOString().split('T')[0] : null
    },
    horseCountRange: {
      min: horseCounts.length > 0 ? Math.min(...horseCounts) : 1,
      max: horseCounts.length > 0 ? Math.max(...horseCounts) : 30
    }
  };
};

/**
 * Create indexed data structure for efficient filtering
 * @param {Array} races - Race array
 * @returns {Object} Indexed race data
 */
export const createRaceIndex = (races) => {
  const index = {
    byState: new Map(),
    byTrack: new Map(),
    byDate: new Map(),
    byDayOfWeek: new Map(),
    byHorseCount: new Map()
  };

  races.forEach((race, idx) => {
    // Index by state
    const state = race.state || race.trackState || extractStateFromTrack(race.track);
    if (!index.byState.has(state)) index.byState.set(state, []);
    index.byState.get(state).push(idx);

    // Index by track
    const track = race.track || race.trackName;
    if (!index.byTrack.has(track)) index.byTrack.set(track, []);
    index.byTrack.get(track).push(idx);

    // Index by date
    const date = race.date || race.raceDate;
    if (date) {
      const dateKey = new Date(date).toISOString().split('T')[0];
      if (!index.byDate.has(dateKey)) index.byDate.set(dateKey, []);
      index.byDate.get(dateKey).push(idx);
    }

    // Index by day of week
    if (date) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[new Date(date).getDay()];
      if (!index.byDayOfWeek.has(dayName)) index.byDayOfWeek.set(dayName, []);
      index.byDayOfWeek.get(dayName).push(idx);
    }

    // Index by horse count
    const horseCount = getHorseCount(race);
    if (!index.byHorseCount.has(horseCount)) index.byHorseCount.set(horseCount, []);
    index.byHorseCount.get(horseCount).push(idx);
  });

  return index;
};

/**
 * Debounced filter application
 * @param {Function} filterFunction - Function to call with filters
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} Debounced function
 */
export const createDebouncedFilter = (filterFunction, delay = 300) => {
  let timeoutId;
  
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      filterFunction(...args);
    }, delay);
  };
};

/**
 * Validate filter configuration
 * @param {Object} filters - Filter object to validate
 * @returns {Object} Validation result with errors
 */
export const validateFilters = (filters) => {
  const errors = [];
  
  if (filters.dateRange) {
    const { start, end } = filters.dateRange;
    if (start && end && new Date(start) > new Date(end)) {
      errors.push('Start date must be before end date');
    }
  }
  
  if (filters.horseCountRange) {
    const { min, max } = filters.horseCountRange;
    if (min !== null && max !== null && min > max) {
      errors.push('Minimum horse count must be less than maximum');
    }
    if (min !== null && min < 1) {
      errors.push('Minimum horse count must be at least 1');
    }
    if (max !== null && max > 30) {
      errors.push('Maximum horse count cannot exceed 30');
    }
  }
  
  if (filters.combinedOddsThreshold !== null && filters.combinedOddsThreshold < 0) {
    errors.push('Combined odds threshold must be positive');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};